import {Injectable, Injector} from '@angular/core';
import {HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpContextToken} from '@angular/common/http';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {
  CheckoutDialogComponent,
  CheckoutDialogData,
  CheckoutDialogResult
} from '@app/shared/dialogs/checkout-dialog/checkout-dialog.component';
import {
  ModuleTierChoiceDialogComponent,
  ModuleTierChoiceDialogData,
  ModuleTierChoiceDialogResult
} from '@app/shared/dialogs/module-tier-choice-dialog/module-tier-choice-dialog.component';
import {ModulePackage} from '@app/interfaces/packages/module-package';
import {ModulePackageActivationSyncService} from '@app/services/util/module-package-activation-sync.service';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {AvtonetInfoDialogComponent} from '@app/shared/dialogs/avtonet-info-dialog/avtonet-info-dialog.component';

// Requests carrying this context set to true (e.g. passive dashboard-widget
// polling that fires on page load with no user action behind it) never get
// the auto-opened upsell dialog — the caller handles the 402 itself instead.
export const SKIP_FEATURE_LOCKED_DIALOG = new HttpContextToken<boolean>(() => false);

const VIRTUAL_SALON_FEATURE_FLAGS: string[] = [
  AccountFeaturesEnum.HAS_VIRTUAL_SALON_BASIC,
  AccountFeaturesEnum.HAS_VIRTUAL_SALON_MODULE_ACCESS,
  AccountFeaturesEnum.HAS_VIRTUAL_SALON_PRO,
];

interface PendingFeatureLockedRequest {
  originalReq: HttpRequest<any>;
  next: HttpHandler;
  result$: Subject<HttpEvent<any>>;
}

@Injectable()
export class FeatureLockedInterceptor implements HttpInterceptor {
  // Keyed by module_package_uuid, so concurrent 402s for the same locked
  // feature (e.g. several guarded API calls firing on the same page load)
  // share a single dialog instead of stacking duplicates. MatDialogRef<any>
  // because this now holds either a CheckoutDialogComponent ref (single
  // package) or a ModuleTierChoiceDialogComponent ref (multiple packages) —
  // only .afterClosed() is ever called on it, so the broader type is safe.
  private openDialogs = new Map<string, MatDialogRef<any>>();
  // The virtual-salon coming-soon path only calls openDialogs.set() after an async
  // getAccountConfig() resolves, leaving a window where two concurrent 402s for the same
  // key both pass the openDialogs.has() check below before either has resolved. Reserve
  // the key here, synchronously, the moment we commit to handling it.
  private reservedKeys = new Set<string>();
  private pendingRequests = new Map<string, PendingFeatureLockedRequest[]>();

  constructor(
    private injector: Injector
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 402 && error.error?.error === 'feature_locked' && !req.context.get(SKIP_FEATURE_LOCKED_DIALOG)) {
          return this.handleFeatureLocked(error, req, next);
        }
        return throwError(error);
      })
    );
  }

  private handleFeatureLocked(
    error: HttpErrorResponse,
    originalReq: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const body = error.error ?? {};
    const key = this.resolveKey(body);
    const result$ = new Subject<HttpEvent<any>>();

    const waiters = this.pendingRequests.get(key) ?? [];
    waiters.push({originalReq, next, result$});
    this.pendingRequests.set(key, waiters);

    if (this.openDialogs.has(key) || this.reservedKeys.has(key)) {
      return result$.asObservable();
    }
    this.reservedKeys.add(key);

    const matDialog = this.injector.get(MatDialog);

    const settle = (dialogResult: CheckoutDialogResult | undefined) => {
      this.openDialogs.delete(key);
      this.reservedKeys.delete(key);
      const settledWaiters = this.pendingRequests.get(key) ?? [];
      this.pendingRequests.delete(key);

      if (dialogResult?.success && dialogResult?.activated) {
        const activationSync = this.injector.get(ModulePackageActivationSyncService);
        activationSync.syncAfterChange();

        settledWaiters.forEach((waiter) => {
          waiter.next.handle(waiter.originalReq).subscribe({
            next: (event) => waiter.result$.next(event),
            error: (err) => waiter.result$.error(err),
            complete: () => waiter.result$.complete()
          });
        });
      } else {
        settledWaiters.forEach((waiter) => {
          waiter.result$.error(error);
          waiter.result$.complete();
        });
      }
    };

    const rawPackages: any[] = Array.isArray(body.module_packages) && body.module_packages.length > 0
      ? body.module_packages
      : [{
          module_package_uuid: body.module_package_uuid,
          module_package_name: body.module_package_name,
          module_type: body.module_type,
          price: body.price,
          price_monthly: body.price_monthly,
          price_yearly: body.price_yearly,
          yearly_discount_pct: body.yearly_discount_pct,
        }];

    const packages: ModulePackage[] = rawPackages.map((p: any) => this.toModulePackage(p));

    // No resolvable module package to upsell (e.g. no active ModulePackage matches this
    // account's country for the locked feature) — opening the checkout dialog anyway would
    // just fail with a raw backend validation error. Treat this as "feature genuinely
    // unavailable, nothing to sell" and let the original error propagate instead.
    if (packages.every((p) => !p.uuid)) {
      settle(undefined);
      return result$.asObservable();
    }

    if (body.feature && VIRTUAL_SALON_FEATURE_FLAGS.includes(body.feature)) {
      const configApi = this.injector.get(ConfigAPIService);
      configApi.getAccountConfig().subscribe({
        next: (config) => {
          if (config.accountFeatureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_COMING_SOON]) {
            const comingSoonDialogRef = AvtonetInfoDialogComponent.open(matDialog, {
              title: $localize`:@@advertising.virtual-salon.hero.coming-soon.title:Spletna stran kmalu na voljo`,
              intro: $localize`:@@advertising.virtual-salon.hero.coming-soon.intro:Ta funkcionalnost bo kmalu na voljo tudi za vaš račun. Obveščali te bomo, ko bo pripravljena.`,
              steps: [],
            });
            this.openDialogs.set(key, comingSoonDialogRef);
            comingSoonDialogRef.afterClosed().subscribe(() => settle(undefined));
            return;
          }
          this.openFeatureLockedDialog(key, packages, matDialog, settle);
        },
        error: () => settle(undefined)
      });
      return result$.asObservable();
    }

    this.openFeatureLockedDialog(key, packages, matDialog, settle);
    return result$.asObservable();
  }

  private openFeatureLockedDialog(
    key: string,
    packages: ModulePackage[],
    matDialog: MatDialog,
    settle: (dialogResult: CheckoutDialogResult | undefined) => void
  ): void {
    const openCheckout = (modulePackage: ModulePackage) => {
      const dialogRef = CheckoutDialogComponent.open(matDialog, {
        type: 'module_package',
        modulePackage,
      } as CheckoutDialogData);
      dialogRef.afterClosed().subscribe((dialogResult: CheckoutDialogResult) => settle(dialogResult));
    };

    if (packages.length > 1) {
      const tierDialogRef = ModuleTierChoiceDialogComponent.open(matDialog, {
        packages,
        currentPackageUuid: null
      } as ModuleTierChoiceDialogData);
      this.openDialogs.set(key, tierDialogRef);

      tierDialogRef.afterClosed().subscribe((tierResult: ModuleTierChoiceDialogResult) => {
        if (!tierResult?.selectedPackage) {
          settle(undefined);
          return;
        }
        openCheckout(tierResult.selectedPackage);
      });
    } else {
      const dialogRef = CheckoutDialogComponent.open(matDialog, {
        type: 'module_package',
        modulePackage: packages[0],
      } as CheckoutDialogData);
      this.openDialogs.set(key, dialogRef);
      dialogRef.afterClosed().subscribe((dialogResult: CheckoutDialogResult) => settle(dialogResult));
    }
  }

  // Dedup key for coalescing concurrent 402s for the same locked feature into one dialog. The
  // legacy single-package shape has module_package_uuid; the newer multi-tier shape instead
  // returns a module_packages array with no top-level module_package_uuid at all — falling back
  // to the literal string 'unknown' there would collapse every distinct multi-tier lock set
  // (e.g. Basic-vs-Pro Virtual Salon locked at the same time as some unrelated locked module)
  // into a single shared dialog. Derive a key from the sorted package uuids instead so different
  // multi-tier sets stay distinct.
  private resolveKey(body: any): string {
    if (body.module_package_uuid) {
      return body.module_package_uuid;
    }
    if (Array.isArray(body.module_packages) && body.module_packages.length > 0) {
      return body.module_packages
        .map((p: any) => p?.module_package_uuid ?? '')
        .sort()
        .join(',');
    }
    return 'unknown';
  }

  private toModulePackage(p: any): ModulePackage {
    return {
      uuid: p.module_package_uuid,
      name: p.module_package_name,
      pricePerUnit: p.price ?? null,
      description: null,
      isActive: true,
      moduleCode: '',
      moduleType: p.module_type ?? null,
      billingUnit: null,
      includedUnits: null,
      hasSubscriptionOption: false,
      priceMonthly: p.price_monthly ?? null,
      priceYearly: p.price_yearly ?? null,
      yearlyDiscountPct: p.yearly_discount_pct ?? null,
      featureFlags: null,
      countryCode: null,
      isPreferred: false,
      isUpsellable: true,
      order: null,
      isFree: false,
      createdAt: '',
      updatedAt: ''
    };
  }
}
